import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { safeAxios } from '@/services/safeAxios';
import { API_ROUTES } from '@/api/apiRoutes';
import type { Recipe } from '@/types/api/recipe';

/** Fetches a workflow at a specific version, or its current draft when `version` is omitted. */
export const fetchWorkflowContent = async (workflowId: string, version?: number): Promise<Recipe> => {
  const { data } = await safeAxios.get<Recipe>(API_ROUTES.recipes.byId({ recipeId: workflowId }), {
    params: { version },
  });
  return data;
};

const byId = (a: { id: string }, b: { id: string }) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Strips transient UI state and normalises ordering so two versions compare on content alone. */
const toComparableContent = (workflow: Recipe) => ({
  nodes: workflow.nodes
    .map((node) => ({ ...omit(node, 'selected'), data: omit(node.data, 'latestPrediction') }))
    .sort(byId),
  edges: workflow.edges.map((edge) => omit(edge, 'selected')).sort(byId),
  designAppMetadata: workflow.designAppMetadata ?? null,
  // Without this a tool-only edit — an output added, an input hidden — never reads as a change, so
  // a published tool would show no update available for the thing the builder actually changed.
  // `metadata` is the tool's name, description and thumbnail, parked here only until the first
  // deploy copies it onto the tool row, which is what viewers read. No published version renders it,
  // so counting it would show "update available" and push a republish for a change nobody sees.
  ...comparableToolConfig(workflow),
});

/**
 * The tool config as it bears on what the tool serves. A version without a lock snapshot reads as if it
 * took one now, every locked node, so taking the snapshot on a draft (just opening it with the flag on) is not a
 * change, while exposing a node that was locked in it is.
 */
const comparableToolConfig = (workflow: Recipe) => {
  const { metadata: _metadata, lockedAtConversion, ...declaration } = workflow.toolConfig ?? {};
  const lockedIds = lockedAtConversion ?? workflow.nodes.filter((node) => node.data?.isLocked).map((node) => node.id);
  return {
    toolConfig: Object.keys(declaration).length > 0 ? declaration : null,
    lockedAtConversion: [...lockedIds].sort(),
  };
};

export const hasWorkflowContentChanged = (a: Recipe, b: Recipe): boolean =>
  !isEqual(toComparableContent(a), toComparableContent(b));
